import { createUserQueryOptions, usersApi } from "@/api/users.api";
import { AlertModal } from "@/components/generic/AlertModal";
import { Button } from "@/components/generic/Button";
import { CheckCard } from "@/components/generic/CheckCard";
import { Input } from "@/components/generic/Field";
import { Select, SelectItem } from "@/components/generic/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { SettingsContainer } from "@/components/layout/SettingsContainer";
import { getUserRoleLevel, userRoleHierarchy } from "@/services/auth.service";
import { User } from "@/types/auth";
import { UserRole, userRoleLabels } from "@/types/user";
import { PagedResult, queryClient } from "@/utils/api";
import { zodResolver } from "@hookform/resolvers/zod";
import { InfiniteData, useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { TrashIcon, UserRoundX } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Form } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";

export const Route = createFileRoute("/_dashboard/settings/_admin/users/$id/")({
  component: RouteComponent,
  staticData: {
    isBreadcrumbRoot: true,
  },
  beforeLoad: async ({ context, params }) => {
    const userQueryOptions = createUserQueryOptions(params.id);
    const targetUser =
      await context.queryClient.ensureQueryData(userQueryOptions);

    if (targetUser.roles.includes(UserRole.LassoAdmin))
      throw redirect({
        to: "/settings/users",
      });

    const currentUserRoleLevel = getUserRoleLevel(context.user);
    const targetUserRoleLevel = getUserRoleLevel(targetUser);

    if (targetUserRoleLevel > currentUserRoleLevel)
      throw redirect({
        to: "/settings/users",
      });

    return {
      useTargetUserQuery: () =>
        useQuery({
          ...userQueryOptions,
          initialData: targetUser,
        }),
    };
  },
  loader: async () => ({
    crumb: "Users",
  }),
});

const salesRoles = [
  UserRole.Sales,
  UserRole.Salesleader,
  UserRole.Admin,
] as const;

const formSchema = z.object({
  name: z.string(),
  email: z.string(),
  salesRole: z.enum(salesRoles),
  hasInsightsAccess: z.boolean(),
  hasAllCampaignAccess: z.boolean(),
});

function RouteComponent() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { user: currentUser, useTargetUserQuery } = Route.useRouteContext();
  const { data: targetUser } = useTargetUserQuery();

  const salesRoleOptions = useMemo(() => {
    const currentUserRoleLevel = getUserRoleLevel(currentUser);

    // Only show roles that are the same or lower level than the current user.
    const filteredSalesRoles = salesRoles.filter((role) => {
      const roleLevel = userRoleHierarchy.get(role) ?? -1;
      return roleLevel <= currentUserRoleLevel;
    });

    return filteredSalesRoles.map((role) => ({
      id: role,
      label: userRoleLabels[role],
    }));
  }, [currentUser]);

  const [deleteUser, setDeleteUser] = useState(false);
  const [deactiveUser, setDeactiveUser] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      salesRole: UserRole.Sales as const,
      hasInsightsAccess: false,
      hasAllCampaignAccess: false,
    },
  });

  const formData = watch();

  const defaultFormData = useMemo((): z.infer<typeof formSchema> => {
    const roles = new Set(targetUser.roles);
    return {
      name: targetUser.name || "",
      email: targetUser.email || "",
      salesRole: roles.has(UserRole.Admin)
        ? UserRole.Admin
        : roles.has(UserRole.Salesleader)
          ? UserRole.Salesleader
          : UserRole.Sales,
      hasInsightsAccess: roles.has(UserRole.User) || roles.has(UserRole.Admin),
      hasAllCampaignAccess:
        roles.has(UserRole.SalesAllCampaignAccess) ||
        roles.has(UserRole.Salesleader) ||
        roles.has(UserRole.Admin),
    };
  }, [targetUser.email, targetUser.name, targetUser.roles]);

  useEffect(() => {
    reset(defaultFormData);
  }, [reset, defaultFormData]);

  const updateUserQueriesData = (updater: (user: User) => User) => {
    queryClient.setQueryData(
      createUserQueryOptions(id).queryKey,
      (user) => user && updater(user),
    );

    const usersQueries = queryClient.getQueriesData<
      InfiniteData<PagedResult<User>>
    >({ queryKey: ["users"], exact: false });

    for (const [queryKey, queryData] of usersQueries) {
      if (!queryData) continue;

      let updated = false;

      const newPages = queryData.pages.map((page) => ({
        ...page,
        results: page.results.map((user) => {
          if (user.id === id) {
            const newUser = updater(user);
            if (newUser !== user) {
              updated = true;
            }
            return newUser;
          }

          return user;
        }),
      }));

      if (updated) {
        queryClient.setQueryData(queryKey, {
          ...queryData,
          pages: newPages,
        });
      }
    }
  };

  const updateUserRolesMutation = useMutation({
    mutationFn: async (payload: UserRole[]) => {
      const updatedRoles = await usersApi.updateUserRoles(id, payload);
      updateUserQueriesData((user) => ({
        ...user,
        roles: updatedRoles,
      }));
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: async (payload: Parameters<typeof usersApi.updateUser>[1]) => {
      const updatedUser = await usersApi.updateUser(id, payload);
      updateUserQueriesData(() => updatedUser);
    },
  });

  const changeUserStateMutation = useMutation({
    mutationFn: async (active: boolean) => {
      await usersApi.setUserState(id, active);
    },
    onSuccess: (_, active) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user", id] });
      toast.success(
        `${targetUser?.name} ${active ? "er nu aktiveret" : " er nu deaktiveret"}`,
      );
      setDeactiveUser(false);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async () => {
      return await usersApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Brugeren blev slettet");
      navigate({ to: "/settings/users" });
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    const newRoles = new Set(targetUser.roles);

    if (data.salesRole !== defaultFormData.salesRole) {
      for (const role of salesRoles) {
        if (role !== data.salesRole) {
          newRoles.delete(role);
        } else {
          newRoles.add(role);
        }
      }
    }

    if (data.salesRole !== UserRole.Admin) {
      if (data.hasInsightsAccess) {
        newRoles.add(UserRole.User);
      } else {
        newRoles.delete(UserRole.User);
      }
    }

    if (
      data.salesRole !== UserRole.Admin &&
      data.salesRole !== UserRole.Salesleader
    ) {
      if (data.hasAllCampaignAccess) {
        newRoles.add(UserRole.SalesAllCampaignAccess);
      } else {
        newRoles.delete(UserRole.SalesAllCampaignAccess);
      }
    }

    await Promise.all([
      updateUserRolesMutation.mutateAsync([...newRoles]),
      updateUserMutation.mutateAsync({
        name: data.name,
      }),
    ]);

    toast.success("Brugeren blev opdateret");

    await navigate({ to: "/settings/users" });
  });

  return (
    <>
      <PageHeader
        backLinkOptions={{
          from: Route.fullPath,
          to: "/settings/users",
        }}
        breadcrumbs={{
          className: "uppercase",
          from: Route.fullPath,
          fromCrumb: targetUser.name,
        }}
        sticky={false}
      />
      <SettingsContainer
        className="mx-14 mt-6 mb-6 p-0!"
        backLinkOptions={{ to: "/settings/users" }}
      >
        <Form onSubmit={onSubmit} className="flex w-full flex-col items-center">
          <div className="flex w-full flex-col items-center justify-center gap-10">
            <div className="flex max-w-md flex-col justify-center">
              <h1 className="text-md text-center font-medium">
                Brugerdetaljer
              </h1>
              <span className="text-theme-on-background-variant mt-6 text-center text-sm font-light">
                Vælg brugerens rolle og om de skal have adgang til Insights.
              </span>
            </div>

            <div className="flex w-full max-w-sm flex-col gap-4">
              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Indtast navn"
                    type="text"
                    className="border-theme-outline-variant"
                    aria-invalid={!!errors.name}
                  />
                )}
              />

              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <Input
                    disabled={true}
                    {...field}
                    type="email"
                    className="border-theme-outline-variant/70 text-theme-outline cursor-not-allowed"
                    aria-invalid={!!errors.email}
                  />
                )}
              />

              <Controller
                control={control}
                name="salesRole"
                render={({ field }) => (
                  <Select
                    selectedKey={field.value}
                    onSelectionChange={(key) => field.onChange(key)}
                    items={salesRoleOptions}
                    className="border-theme-outline-variant"
                  >
                    {(item) => (
                      <SelectItem id={item.id} textValue={item.label}>
                        {item.label}
                      </SelectItem>
                    )}
                  </Select>
                )}
              />

              <CheckCard
                control={control}
                name="hasInsightsAccess"
                label="Adgang til Insights"
                {...(formData.salesRole === UserRole.Admin
                  ? {
                      isSelected: true,
                      isDisabled: true,
                    }
                  : {})}
              />

              <CheckCard
                control={control}
                name="hasAllCampaignAccess"
                label="Kan se alle kampagner og leads"
                {...(formData.salesRole === UserRole.Salesleader ||
                formData.salesRole === UserRole.Admin
                  ? {
                      isSelected: true,
                      isDisabled: true,
                    }
                  : {})}
              />
            </div>

            <div className="flex flex-col items-center">
              <div className="mb-6 flex gap-4">
                <Button
                  onPress={() =>
                    targetUser.active
                      ? setDeactiveUser(true)
                      : changeUserStateMutation.mutate(true)
                  }
                  animate
                  intent="secondary"
                >
                  {targetUser.active ? "Deaktiver" : "Aktiver"} bruger
                </Button>

                <Button
                  type="submit"
                  animate
                  isDisabled={!isDirty || isSubmitting}
                >
                  Gem
                </Button>
              </div>

              <Button
                onPress={() => setDeleteUser(true)}
                intent="tertiary"
                className="cursor-pointer"
              >
                Slet brugeren
              </Button>
            </div>
          </div>
        </Form>
      </SettingsContainer>

      <AlertModal
        isOpen={deleteUser}
        title="Slet brugeren"
        description="Er du sikker på, at du vil slette denne bruger?"
        disableConfirmButton={deleteUserMutation.isPending}
        onClose={() => setDeleteUser(false)}
        onConfirm={() => {
          deleteUserMutation.mutate();
        }}
        icon={<TrashIcon className="text-theme-active" />}
      />

      <AlertModal
        isOpen={deactiveUser}
        title="Deaktiver bruger"
        description="Er du sikker på, at du vil deaktivere brugeren?"
        disableConfirmButton={changeUserStateMutation.isPending}
        onClose={() => setDeactiveUser(false)}
        onConfirm={() => changeUserStateMutation.mutate(!targetUser.active)}
        icon={<UserRoundX className="text-theme-active" />}
      />
    </>
  );
}
